import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue'
import { useWindowSize } from '@vueuse/core'

import LegPickerSheetDesktop from './legPicker/LegPickerSheetDesktop.vue'
import LegPickerSheetMobile from './legPicker/LegPickerSheetMobile.vue'
import type { GroupAnchorDraft } from '@/stores/transportation'
import { useTransportationGroupStore } from '@/stores/transportation-group'
import type { Transportation } from '@/types/transportation'

interface Props {
  open: boolean
  /** Segment currently being edited (used for seed-lock and group lookup). */
  transportation?: Pick<Transportation, '_id' | 'type' | 'groupId'>
  /** WP id at which the current segment lives — seed leg, locked. */
  seedFromWaypointId?: string
  /** v-model: selected fromWaypointIds. Live two-way bound with parent form. */
  value: string[]
  arrival?: GroupAnchorDraft
  departure?: GroupAnchorDraft
  seedIsArrival?: boolean
  seedIsDeparture?: boolean
  seedOriginName?: string
  seedDestinationName?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LegPickerSheet',
  props: {
    open: { type: Boolean },
    transportation: null,
    seedFromWaypointId: null,
    value: null,
    arrival: { default: () => ({ enabled: false }) },
    departure: { default: () => ({ enabled: false }) },
    seedIsArrival: { type: Boolean },
    seedIsDeparture: { type: Boolean },
    seedOriginName: null,
    seedDestinationName: null
  },
  emits: ["input", "update:arrival", "update:departure", "close"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'input', selection: string[]): void
  (e: 'update:arrival', value: GroupAnchorDraft): void
  (e: 'update:departure', value: GroupAnchorDraft): void
  (e: 'close'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { open: boolean, transportation?: Pick<Transportation, '_id' | 'type' | 'groupId'>, seedFromWaypointId?: string, value: string[], arrival: GroupAnchorDraft, departure: GroupAnchorDraft, seedIsArrival?: boolean, seedIsDeparture?: boolean, seedOriginName?: string, seedDestinationName?: string };




// Floating layout needs: drawer (32rem = 512px) + gap (1rem = 16px) +
// sheet (520px) = 1048px. Add small right buffer → 1064px threshold.
const DESKTOP_MIN_WIDTH = 1064

const { width } = useWindowSize()
const variant = computed(() =>
  width.value >= DESKTOP_MIN_WIDTH
    ? LegPickerSheetDesktop
    : LegPickerSheetMobile,
)
const groupStore = useTransportationGroupStore()
const existingGroup = computed(() =>
  props.transportation
    ? groupStore.getGroupForTransportation(props.transportation)
    : undefined,
)
const arrivalNeedsExternalOrigin = computed(
  () =>
    props.arrival.enabled &&
    !existingGroup.value?.hasArrivalTransportation &&
    !props.seedIsArrival,
)
const departureNeedsExternalDestination = computed(
  () =>
    props.departure.enabled &&
    !(existingGroup.value
      ? groupStore.hasDepartureTransportation(existingGroup.value)
      : false) &&
    !props.seedIsDeparture,
)

const anchorErrors = ref({
  arrivalOrigin: false,
  departureDestination: false,
})

function validateAnchors() {
  anchorErrors.value = {
    arrivalOrigin:
      arrivalNeedsExternalOrigin.value && !props.arrival.location?.location,
    departureDestination:
      departureNeedsExternalDestination.value &&
      !props.departure.location?.location,
  }

  return (
    !anchorErrors.value.arrivalOrigin &&
    !anchorErrors.value.departureDestination
  )
}

function onArrivalUpdate(value: GroupAnchorDraft) {
  if (!value.enabled || value.location?.location) {
    anchorErrors.value.arrivalOrigin = false
  }
  emit('update:arrival', value)
}

function onDepartureUpdate(value: GroupAnchorDraft) {
  if (!value.enabled || value.location?.location) {
    anchorErrors.value.departureDestination = false
  }
  emit('update:departure', value)
}

expose({
  validateAnchors,
})

return { __sfc: true,props, emit, DESKTOP_MIN_WIDTH, width, variant, groupStore, existingGroup, arrivalNeedsExternalOrigin, departureNeedsExternalDestination, anchorErrors, validateAnchors, onArrivalUpdate, onDepartureUpdate }
}

})